import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISPUTE_TYPES = [
  { value: 'damage', label: 'Avarie / Casse' },
  { value: 'missing', label: 'Manquant / Quantité incorrecte' },
  { value: 'delay', label: 'Retard significatif' },
  { value: 'other', label: 'Autre' },
];

/**
 * Modal POD — Validation client de la livraison (Phase 3b, PM Spec 07/05/2026)
 *
 * 2 modes : 'validate' | 'dispute'
 *
 * Props:
 *   open, onOpenChange, mode (validate|dispute),
 *   targetKind: 'operation' | 'transfer'
 *   operationId (si operation), contractId+runId (si transfer)
 *   label (numéro affiché à l'utilisateur)
 *   onSuccess()
 */
const PodValidationModal = ({
  open, onOpenChange, mode = 'validate',
  targetKind, operationId, contractId, runId, label,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [validateComment, setValidateComment] = useState('');
  const [disputeType, setDisputeType] = useState('damage');
  const [disputeComment, setDisputeComment] = useState('');
  const [photoUrls, setPhotoUrls] = useState([]); // saisie d'URL (MVP)
  const [photoInput, setPhotoInput] = useState('');

  const buildUrl = (action) => {
    if (targetKind === 'operation') {
      return `${API}/client/operations/${operationId}/pod/${action}`;
    }
    return `${API}/client/contracts/${contractId}/transfers/${runId}/pod/${action}`;
  };

  const reset = () => {
    setValidateComment('');
    setDisputeType('damage');
    setDisputeComment('');
    setPhotoUrls([]); setPhotoInput('');
  };

  const submitValidate = async () => {
    setSubmitting(true);
    try {
      await axios.post(buildUrl('validate'), { comment: validateComment || undefined },
        {});
      toast.success('Livraison validée. Merci !');
      reset(); onOpenChange?.(false); onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDispute = async () => {
    if (!disputeComment || disputeComment.trim().length < 5) {
      toast.error('Merci de décrire le problème (5 caractères minimum).');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(buildUrl('dispute'), {
        disputeType,
        comment: disputeComment.trim(),
        photos: photoUrls,
      }, {});
      toast.success('Litige enregistré. Notre équipe va vous contacter rapidement.');
      reset(); onOpenChange?.(false); onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement du litige');
    } finally {
      setSubmitting(false);
    }
  };

  const addPhoto = () => {
    const u = photoInput.trim();
    if (!u) return;
    if (!u.startsWith('http')) {
      toast.error('URL invalide (doit commencer par http/https)');
      return;
    }
    setPhotoUrls(prev => [...prev, u]);
    setPhotoInput('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) { onOpenChange?.(v); if (!v) reset(); } }}>
      <DialogContent className="max-w-lg" data-testid={`pod-${mode}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'validate' ? (
              <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Valider la livraison</>
            ) : (
              <><AlertTriangle className="w-5 h-5 text-amber-600" /> Signaler un litige</>
            )}
            <span className="text-sm font-normal text-slate-500">— {label}</span>
          </DialogTitle>
          <DialogDescription>
            {mode === 'validate'
              ? 'Confirmez que la livraison a été reçue conformément à votre commande.'
              : 'Décrivez précisément le problème pour que notre équipe puisse intervenir.'}
          </DialogDescription>
        </DialogHeader>

        {mode === 'validate' && (
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600 bg-emerald-50 border border-emerald-100 rounded-md p-3">
              ✓ Le bon de déchargement a bien été signé. <br />
              En validant, vous confirmez la conformité de la livraison.
            </p>
            <div>
              <Label className="text-xs text-slate-500">Commentaire (optionnel)</Label>
              <Textarea
                rows={3}
                value={validateComment}
                onChange={(e) => setValidateComment(e.target.value)}
                placeholder="Tout est conforme. Merci pour la qualité du service…"
                data-testid="pod-validate-comment"
              />
            </div>
          </div>
        )}

        {mode === 'dispute' && (
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs text-slate-500">Type de litige *</Label>
              <Select value={disputeType} onValueChange={setDisputeType}>
                <SelectTrigger data-testid="pod-dispute-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISPUTE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-slate-500">Description détaillée *</Label>
              <Textarea
                rows={4}
                value={disputeComment}
                onChange={(e) => setDisputeComment(e.target.value)}
                placeholder="Précisez le contexte, le nombre/quantité concerné, l'impact…"
                data-testid="pod-dispute-comment"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-500">
                Photos preuve (URLs · optionnel) — {photoUrls.length} ajoutée{photoUrls.length > 1 ? 's' : ''}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={photoInput}
                  onChange={(e) => setPhotoInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhoto(); } }}
                  placeholder="https://…"
                  data-testid="pod-dispute-photo-input"
                />
                <Button type="button" variant="outline" onClick={addPhoto} disabled={!photoInput.trim()}>
                  Ajouter
                </Button>
              </div>
              {photoUrls.length > 0 && (
                <div className="mt-2 space-y-1">
                  {photoUrls.map((u, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded p-1.5">
                      <span className="truncate flex-1">{u}</span>
                      <button onClick={() => setPhotoUrls(prev => prev.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange?.(false); }} disabled={submitting}>
            Annuler
          </Button>
          {mode === 'validate' ? (
            <Button onClick={submitValidate} disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="pod-validate-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Valider la livraison
            </Button>
          ) : (
            <Button onClick={submitDispute} disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="pod-dispute-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Envoyer le litige
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PodValidationModal;
